import { Coordinate } from 'ol/coordinate';
import type { FeatureCollection } from 'geojson';
import { HeaderTimestamp, RosBoundary, RosPosition } from '../types/rosmsg';
import { alertSuccess } from '../alertDispatcher';
import { dispatchRosMsgPub } from '../RobotConnection';
import { getCurrentSession } from '../dataModelHelpers';
import { capitalizeFirstLetter } from '../utils';
import type { OutboundTopic } from '../types/rosTopics';

export function rosPositionToCoordinates(rosPosition: RosPosition): Coordinate {
  return [rosPosition.x, rosPosition.y];
}

export function rosTimestampToJsDate(rosTimestamp: HeaderTimestamp) {
  return new Date(rosTimestamp.secs * 1000 + rosTimestamp.nsecs / 1000000);
}

export function sendROSConfig<T extends string | number | boolean>(
  topic: OutboundTopic,
  newValue: T,
  label: string,
  currentValue: T,
  checkBeforeSet: boolean,
  convert?: (newValue: T) => T,
  expectTopic?: string,
) {
  if (checkBeforeSet && newValue === currentValue) {
    alertSuccess(`${capitalizeFirstLetter(label)} already set to ${newValue}!`);

    return;
  }

  const session = getCurrentSession();
  dispatchRosMsgPub({
    hostname: session?.robot_hostname || '',
    msg: { data: convert ? convert(newValue) : newValue },
    topic,
    tag: Math.random().toString(36),
    expectationParams: {
      expectedTopic: expectTopic ?? topic.replace('/set_', '/'),
      messages: {
        success: `Successfully set ${label} from ${currentValue} to ${newValue}!`,
        error: `Failed to set ${label} from ${currentValue} to ${newValue}!`,
      },
    },
  });
}

export function genBoundarySentToRobot(
  outerBoundaries: RosBoundary[],
  innerBoundaries: RosBoundary[],
): FeatureCollection {
  const boundaryFeatureCollection: FeatureCollection = {
    type: 'FeatureCollection',
    features: [],
  };

  const outerCoords = outerBoundaries.map((b) => {
    return [b.points.map((coord) => [coord.x, coord.y])];
  });

  boundaryFeatureCollection.features.push({
    type: 'Feature',
    properties: { boundaryType: 'outer' },
    geometry: {
      type: 'MultiPolygon',
      coordinates: outerCoords,
    } as any,
  } as any);

  const innerCoords = innerBoundaries.map((b) => {
    return [b.points.map((coord) => [coord.x, coord.y])];
  });

  boundaryFeatureCollection.features.push({
    type: 'Feature',
    properties: { boundaryType: 'inner' },
    geometry: {
      type: 'MultiPolygon',
      coordinates: innerCoords,
    } as any,
  } as any);

  return boundaryFeatureCollection;
}
